import {
  AccordionItem,
  AccordionItemContent,
  AccordionItemTrigger,
  AccordionRoot,
} from '@/components/ui';
import { useAuth, useBreadcrumbs } from '@/hooks';
import { trans } from '@/i18n';
import { appMenus, IMenu, MENU_LOGOUT, ROUTE_PATH } from '@/routes';
import { Flex, Heading, Icon, Text, VStack } from '@chakra-ui/react';
import { useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

export const menuWidth = 233;
export const menuSmallWidth = 69;

interface IProps {
  collapse: boolean;
  onToggleCollapse: () => void;
}

function LayoutSidebar({ collapse }: IProps) {
  const { logout, isAdmin } = useAuth();
  const { activeMenu } = useBreadcrumbs();
  const location = useLocation();
  const navigate = useNavigate();

  const pathName = location.pathname;

  const handleMenuClick = useCallback((key: string) => {
    if (key !== MENU_LOGOUT) return;
    navigate(ROUTE_PATH.LOGIN);
    logout();
  }, []);

  const isParentMenuActive = useCallback(
    (menuItem: IMenu) => {
      if (activeMenu === menuItem.key) return true;

      if (menuItem.children) {
        return menuItem.children.some((child: IMenu) => {
          if (isAdmin && child.key === ROUTE_PATH.ADMIN_CREATE) {
            return false;
          }
          return pathName === child.key;
        });
      }

      return false;
    },
    [activeMenu, pathName, isAdmin]
  );

  const handleParentMenuClick = useCallback(
    (item: IMenu) => {
      if (collapse && item.children && item.children.length > 0) {
        const firstChild = item.children.find((child: IMenu) => {
          if (isAdmin && child.key === ROUTE_PATH.ADMIN_CREATE) {
            return false;
          }
          return true;
        });
        if (firstChild) {
          navigate(firstChild.key);
        }
      }
    },
    [collapse, isAdmin, navigate]
  );

  return (
    <VStack
      width={collapse ? menuSmallWidth : menuWidth}
      minW={collapse ? menuSmallWidth : menuWidth}
      height="calc(100vh - 34px)"
      className="left-menu"
      transition="width 0.3s"
      p={'0'}
      gap={'0'}
    >
      <NavLink to={ROUTE_PATH.HOME} style={{ paddingTop: '30px', paddingBottom: '32px' }}>
        <Heading
          fontWeight="extrabold"
          cursor="pointer"
          style={{ height: 'fit-content', lineHeight: '100%' }}
        >
          <Text fontSize="12px" color="#6A6A6A" fontWeight="bold">
            {trans('menu.logo')}
          </Text>
        </Heading>
      </NavLink>

      <div className="gradient-thick" style={{ marginBottom: '30px' }} />

      <AccordionRoot multiple collapsible className="menu-accordion">
        {appMenus.map((item) => (
          //  @ts-ignore
          <AccordionItem key={item.key} value={item.key}>
            {/* @ts-ignore */}
            <AccordionItemTrigger
              className={isParentMenuActive(item) ? 'active' : ''}
              onClick={() => handleParentMenuClick(item)}
            >
              <div className="m-icon">
                <Icon>{item.icon}</Icon>
              </div>
              <span className="menu-label">{item.label}</span>
            </AccordionItemTrigger>
            {item.children && (
              <AccordionItemContent>
                {item.children
                  ?.filter((item) => {
                    if (isAdmin && item.key == ROUTE_PATH.ADMIN_CREATE) {
                      return false;
                    }
                    return true;
                  })
                  .map((child) => {
                    return (
                      <Flex
                        alignItems="center"
                        className={pathName === child.key ? 'active' : ''}
                        key={child.key}
                        gap={'20px'}
                      >
                        <div
                          className="dot-container"
                          style={{ display: collapse ? 'none' : undefined }}
                        >
                          <div className="dot" />
                        </div>
                        <NavLink
                          to={child.key}
                          key={child.key}
                          className="submenu-item"
                          onClick={() => handleMenuClick(child.key)}
                        >
                          <span
                            className="short-label"
                            style={{ display: !collapse ? 'none' : undefined }}
                          >
                            {child.shortLabel}
                          </span>
                          <span
                            className="full-label"
                            style={{ display: collapse ? 'none' : undefined }}
                          >
                            {child.label}
                          </span>
                        </NavLink>
                      </Flex>
                    );
                  })}
              </AccordionItemContent>
            )}
          </AccordionItem>
        ))}
      </AccordionRoot>

      <div className="gradient-thick" style={{ marginTop: '23px' }} />
    </VStack>
  );
}

export default LayoutSidebar;
